<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrGenicUiStarFlow from "~/components/molecules/WrGenicUiStarFlow.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

defineProps({
    heading: {
        type: String,
        default: "The Problem It Solves",
    },
    subheading: {
        type: String,
        default: "The Problem It Solves",
    },
    intro: {
        type: String,
        default: "",
    },
    problemLabel: {
        type: String,
        default: "",
    },
    problems: {
        type: Array as () => string[],
        default: () => [],
    },
    solutionLabel: {
        type: String,
        default: "",
    },
    solutions: {
        type: Array as () => string[],
        default: () => [],
    },
    quote: {
        type: String,
        default: "",
    },
    ctaText: {
        type: String,
        default: "",
    },
    bgImageDark: {
        type: String,
        default: "/images/genic-ui-comparison-dark.webp",
    },
    bgImageLight: {
        type: String,
        default: "/images/genic-ui-comparison-light.webp",
    },
    bgAlt: {
        type: String,
        default: "Decorative graphic",
    },
});

const emit = defineEmits<{
    (e: "cta-clicked"): void;
}>();
</script>

<template>
    <div class="mt-12 text-center relative overflow-visible">
        <!-- Star flow decoration (behind content) -->
        <WrGenicUiStarFlow />
        <NuxtImg :src="isLight ? bgImageLight : bgImageDark" :alt="bgAlt" loading="lazy" width="947" height="332"
            class="absolute -translate-x-1/2 w-[400px] lg:w-[800px] pointer-events-none max-w-full max-lg:hidden top-[40%] left-[48%]" />
        <h2
            class="w-full max-w-full font-semibold leading-normal text-center px-4 font-avant-demi bg-gradient-to-r from-[#12DBFF] to-[#BA55FE] bg-clip-text text-transparent">
            {{ heading }}
        </h2>
        <h3 class="text-center flex items-center gap-2 justify-center !font-medium">
            {{ subheading }}
            <WrGradientText :text="' GenicUI'"
                :append-class="'dark:bg-[linear-gradient(90deg,#C4B5FD_0%,#6C6AF6_100%)] py-0'" />
        </h3>
        <p class="self-stretch text-center mt-4 lg:opacity-70 max-w-[600px] mx-auto font-avant font-normal leading-[22px] tracking-[0.36px]"
            :class="isLight ? 'text-black' : 'text-white'">
            {{ intro }}
        </p>

        <div
            class="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[3fr_4fr_3fr] items-start mt-10 gap-8 lg:gap-0 max-md:gap-0 md:max-w-[640px] lg:max-w-[964px] mx-auto px-4">
            <div class="relative z-10 text-left max-md:order-3 max-md:rounded-2xl max-md:p-5 max-md:border"
                :class="isLight ? 'max-md:bg-neutral-50 max-md:border-neutral-200' : 'max-md:bg-white/5 max-md:border-white/10'">
                <h3 class="font-semibold font-avant-demi mb-4 text-[20px] leading-normal"
                    :class="isLight ? 'text-[#4753DC]' : 'text-[#579DFF]'">
                    {{ problemLabel }}
                </h3>
                <ul class="space-y-4">
                    <li v-for="(item, i) in problems" :key="i"
                        class="flex items-center text-left gap-2 font-avant text-[15px] font-normal leading-normal tracking-[0.75px]"
                        :class="isLight ? 'text-black' : 'text-white'">
                        <span class="inline-block w-2 h-2 rounded-full flex-shrink-0 max-md:hidden"
                            :class="isLight ? 'bg-[#4753DC]' : 'bg-purple-400'"></span>
                        <svg class="w-4 h-4 flex-shrink-0 md:hidden" :class="isLight ? 'text-gray-500' : 'text-gray-400'"
                            viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
                        </svg>
                        <span class="opacity-80">{{ item }}</span>
                    </li>
                </ul>
            </div>

            <div class="md:hidden flex justify-center my-4 max-md:order-2">
                <span
                    class="flex items-center justify-center w-9 h-9 rounded-full text-xs font-bold font-avant-demi text-white bg-gradient-to-br from-[#4753DC] to-[#BA55FE]">
                    VS
                </span>
            </div>

            <div class="flex items-center justify-center max-lg:hidden"></div>

            <div class="relative z-10 text-left lg:justify-self-end max-md:order-1 max-md:rounded-2xl max-md:p-5 max-md:border"
                :class="isLight ? 'max-md:bg-[#4753DC]/5 max-md:border-[#4753DC]/20' : 'max-md:bg-[#4753DC]/10 max-md:border-[#579DFF]/25'">
                <h3 class="font-semibold font-avant-demi mb-4 text-[20px] leading-normal"
                    :class="isLight ? 'text-[#4753DC]' : 'text-[#579DFF]'">
                    {{ solutionLabel }}
                </h3>
                <ul class="space-y-4">
                    <li v-for="(item, i) in solutions" :key="i"
                        class="flex items-center text-left gap-2 font-avant text-[15px] font-normal leading-normal tracking-[0.75px]"
                        :class="isLight ? 'text-black' : 'text-white'">
                        <span class="inline-block w-2 h-2 rounded-full flex-shrink-0 max-md:hidden"
                            :class="isLight ? 'bg-[#4753DC]' : 'bg-purple-400'"></span>
                        <svg class="w-4 h-4 flex-shrink-0 md:hidden" :class="isLight ? 'text-emerald-500' : 'text-emerald-400'"
                            viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                stroke-linejoin="round" />
                        </svg>
                        <span class="opacity-80">{{ item }}</span>
                    </li>
                </ul>
            </div>
        </div>

        <div v-if="quote" class="mt-12 max-w-[700px] mx-auto px-4">
            <p class="text-center italic font-avant text-[18px] lg:text-[20px] leading-relaxed"
                :class="isLight ? 'text-[#501D71]' : 'text-[#CDA0FE]'">
                "{{ quote }}"
            </p>
        </div>

        <div v-if="ctaText" class="flex justify-center mt-8">
            <WrButton @click="$emit('cta-clicked')"
                append-class="bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)]">
                {{ ctaText }}
            </WrButton>
        </div>
    </div>
</template>
